import { useMemo, useState, type ReactNode } from 'react';
import type { DsVessel } from '@technohull/shared';
import { useDs, useDsWrite } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, ErrorState, FilterToggle, InlineError, LoadingState, PageHeader, Pill, SearchInput, SyncLabel } from '../components/ui.js';
import { IconCheck } from '../components/icons.js';
import { useCellHistoryHover } from '../components/CellHistory.js';
import { useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { commentStamp } from '../lib/dates.js';

// the stamp is written INTO the shared comment, so it is one fixed form ("6 Oct 2026") for everyone
function stamp(text: string): string {
  return `[${commentStamp()}] ${text}`;
}

/** phone: the comment history clamped to its latest two entries, the rest behind "Show all (n)". */
function CommentHistory({ text }: { text: string }) {
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length <= 2) return <div className="ds-hist-last">{text}</div>;
  return (
    <div className="ds-hist">
      <details className="ds-hist-more">
        <summary><span className="ds-when-shut">Show all ({lines.length})</span><span className="ds-when-open">Show the latest 2</span></summary>
        <div className="ds-hist-all">{text}</div>
      </details>
      <div className="ds-hist-last">{lines.slice(-2).join('\n')}</div>
    </div>
  );
}

function CommentCell({ v, canWrite }: { v: DsVessel; canWrite: boolean }) {
  const write = useDsWrite();
  const [txt, setTxt] = useState('');
  const isPhone = useIsPhone();
  if (!canWrite) return isPhone && v.comments ? <CommentHistory text={v.comments} /> : <span className="muted" style={{ whiteSpace: 'pre-wrap' }}>{v.comments || '-'}</span>;
  return (
    <div className="ds-com">
      {/* writers see what's already there before appending to it */}
      {v.comments && (isPhone ? <CommentHistory text={v.comments} /> : <div className="muted" style={{ whiteSpace: 'pre-wrap', marginBottom: 4 }}>{v.comments}</div>)}
      <div className="ds-com-add" style={{ display: 'flex', gap: 6 }}>
        <input className="field" style={{ flex: 1, minWidth: 120 }} placeholder="Append…" aria-label={`Append comment for ${v.name || v.sap}`} value={txt} onChange={(e) => setTxt(e.target.value)} />
        <button
          className="btn btn-sm"
          disabled={!txt.trim() || write.isPending}
          onClick={() => write.mutate({ rowId: v.rowId, comment: `${v.comments ? v.comments + '\n' : ''}${stamp(txt.trim())}` }, { onSuccess: () => setTxt('') })}
        >
          {write.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
    </div>
  );
}

export function Ds() {
  const { data, isLoading, error, refetch, isFetching } = useDs();
  const write = useDsWrite();
  const { can } = useAuth();
  const fmtDate = useFormatDate();
  const canWrite = can('ds:write');
  const { onEnter: histEnter, onLeave: histLeave, bindPress: histPress, historyButton: histButton, popover: histPopover } = useCellHistoryHover(data?.sheetId);
  // phone: each vessel is a card, and its DS / Programmed boxes become 44px labelled toggles
  const isPhone = useIsPhone();
  const [q, setQ] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [hideLeft, setHideLeft] = useState(true);

  const rows = useMemo(() => {
    let vs = data?.vessels ?? [];
    if (hideLeft) vs = vs.filter((v) => !v.left);
    if (pendingOnly) vs = vs.filter((v) => !v.ds || !v.programmed);
    const query = fold(q.trim());
    if (query) vs = vs.filter((v) => hasText(query, v.sap, v.name, v.model));
    return vs;
  }, [data, q, pendingOnly, hideLeft]);
  // a tick box: bare in the desktop table; on a phone card, a 44px toggle carrying its own caption
  const flag = (box: ReactNode, caption: string): ReactNode =>
    isPhone ? <label className="check-row ds-chk">{box}<span>{caption}</span></label> : box;

  return (
    <>
      <PageHeader title="DS - Programming Status">
        <SearchInput value={q} onChange={setQ} placeholder="Search" width={200} />
        <FilterToggle checked={pendingOnly} onChange={setPendingOnly}>Pending only</FilterToggle>
        <FilterToggle checked={hideLeft} onChange={setHideLeft}>Hide delivered</FilterToggle>
        <span className="muted page-count">{rows.length} vessel{rows.length === 1 ? '' : 's'}</span>
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} /></button>
      </PageHeader>

      {isLoading && <LoadingState label="Loading DS…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}

      {data && (
        <div className="tbl-wrap">
          {/* phone cards (tbl-cards): SAP with model · version, the name, the two toggles with the date under
              Programmed, then the comments. Model / Version are folded into the SAP line there. */}
          <table className="tbl tbl-cards ds-tbl">
            <thead>
              <tr>
                <th>SAP</th>
                <th>Name</th>
                <th>Model</th>
                <th>Version</th>
                <th>DS</th>
                <th>Programmed</th>
                <th>Prog. date</th>
                <th style={{ minWidth: 220 }}>Comments</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.rowId}>
                  <td className="tc-title ds-sap">{v.sap}<span className="ds-sub">{[v.model, v.version].filter(Boolean).join(' · ')}</span></td>
                  <td className="ds-name">{v.name}</td>
                  <td className="tc-hide">{v.model}</td>
                  <td className="tc-hide">{v.version || '-'}</td>
                  <td className={`ds-flag${canWrite ? '' : ' tc-lbl'}`} data-label="DS" onPointerEnter={histEnter(v.rowId, data.cols.ds, 'DS', v.name)} onPointerLeave={histLeave} {...histPress(v.rowId, data.cols.ds, 'DS', v.name)}>
                    {canWrite ? flag(
                      <input type="checkbox" aria-label={`DS for ${v.name || v.sap}`} checked={v.ds} onChange={(e) => write.mutate({ rowId: v.rowId, ds: e.target.checked })} />,
                      'DS',
                    ) : v.ds ? <Pill kind="ok" title="Yes"><IconCheck size={12} /><span className="sr-only">Yes</span></Pill> : '-'}
                    {histButton(v.rowId, data.cols.ds, 'DS', v.name)}
                  </td>
                  <td className={`ds-flag${canWrite ? '' : ' tc-lbl'}`} data-label="Programmed" onPointerEnter={histEnter(v.rowId, data.cols.programmed, 'Programmed', v.name)} onPointerLeave={histLeave} {...histPress(v.rowId, data.cols.programmed, 'Programmed', v.name)}>
                    {canWrite ? flag(
                      <input type="checkbox" aria-label={`Programmed for ${v.name || v.sap}`} checked={v.programmed} onChange={(e) => write.mutate({ rowId: v.rowId, programmed: e.target.checked })} />,
                      'Programmed',
                    ) : v.programmed ? <Pill kind="ok" title="Yes"><IconCheck size={12} /><span className="sr-only">Yes</span></Pill> : '-'}
                    {histButton(v.rowId, data.cols.programmed, 'Programmed', v.name)}
                  </td>
                  <td className={`ds-date tc-lbl${v.programming ? '' : ' tc-hide'}`} data-label="Prog. date">{fmtDate(v.programming) || '-'}</td>
                  <td className="ds-com-td" onPointerEnter={histEnter(v.rowId, data.cols.comments, 'Comments', v.name)} onPointerLeave={histLeave} {...histPress(v.rowId, data.cols.comments, 'Comments', v.name)}><CommentCell v={v} canWrite={canWrite} /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8}><EmptyState title="No vessels match" hint="Try a different search, or turn off Pending only / Hide delivered." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {histPopover}
    </>
  );
}
